import { unwrap, type components } from "@sagot/api-client";
import type { ConversationPriority } from "@sagot/constants";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import { toListQuery, type ConversationStatus, type InboxFilters } from "./inbox-model";
import { optimisticUnsnooze, restoreSnapshot } from "./snooze";

type Schemas = components["schemas"];
export type AddToKnowledgeIn = Schemas["AddToKnowledgeIn"];
export type ConversationPatch = Schemas["ConversationPatch"];

const PAGE_SIZE = 30;

export const inboxKeys = {
  root: (wsId: string) => engageKeys.inbox(wsId),
  list: (wsId: string, filters: InboxFilters) => [...engageKeys.inbox(wsId), "list", filters] as const,
  detail: (wsId: string, id: string) => [...engageKeys.inbox(wsId), "detail", id] as const,
  savedReplies: (wsId: string) => [...engageKeys.inbox(wsId), "saved-replies"] as const,
};

/** `GET /v1/conversations` (cursor pages; `counts` per view come with every page). */
export function useConversationList(filters: InboxFilters, pollMs: number | false) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: inboxKeys.list(wsId, filters),
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/conversations", { params: { query: { ...toListQuery(filters), limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: pollMs,
  });
}

/** `GET /v1/conversations/{id}`: thread, events, visitor, lead, SLA, sources, history. */
export function useConversationDetail(conversationId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: inboxKeys.detail(wsId, conversationId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/conversations/{conversation_id}", { params: { path: { conversation_id: conversationId ?? "" } } })),
    enabled: conversationId !== null,
  });
}

export function useSavedReplies(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: inboxKeys.savedReplies(wsId), queryFn: () => unwrap(api.GET("/v1/saved-replies")), enabled, staleTime: 5 * 60_000 });
}

/** Staff actions on one conversation; every success refreshes the list and the thread. */
export function useConversationActions(conversationId: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const path = { params: { path: { conversation_id: conversationId } } };
  const refresh = () => queryClient.invalidateQueries({ queryKey: inboxKeys.root(wsId) });
  const options = { onSettled: refresh };
  return {
    patch: useMutation({ mutationFn: (body: ConversationPatch) => unwrap(api.PATCH("/v1/conversations/{conversation_id}", { ...path, body })), ...options }),
    setStatus: useMutation({ mutationFn: (status: ConversationStatus) => unwrap(api.PATCH("/v1/conversations/{conversation_id}", { ...path, body: { status, clear_assignee: false } })), ...options }),
    setPriority: useMutation({
      mutationFn: (priority: ConversationPriority) => unwrap(api.PATCH("/v1/conversations/{conversation_id}", { ...path, body: { priority, clear_assignee: false } })),
      ...options,
    }),
    /** Ends a snooze: the thread and the lists show it awake at once, and a failed request puts them back. */
    unsnooze: useMutation({
      mutationFn: () => unwrap(api.POST("/v1/conversations/{conversation_id}/unsnooze", path)),
      onMutate: async () => ({ snapshot: await optimisticUnsnooze(queryClient, wsId, conversationId) }),
      onError: (_error, _vars, context) => { restoreSnapshot(queryClient, context?.snapshot); },
      ...options,
    }),
    takeover: useMutation({ mutationFn: () => unwrap(api.POST("/v1/conversations/{conversation_id}/takeover", path)), ...options }),
    release: useMutation({ mutationFn: () => unwrap(api.POST("/v1/conversations/{conversation_id}/release", path)), ...options }),
    reply: useMutation({ mutationFn: (body: string) => unwrap(api.POST("/v1/conversations/{conversation_id}/messages", { ...path, body: { body, internal: false } })), ...options }),
    note: useMutation({
      mutationFn: (input: { body: string; mentions: string[] }) => unwrap(api.POST("/v1/conversations/{conversation_id}/notes", { ...path, body: input })),
      ...options,
    }),
    draft: useMutation({ mutationFn: () => unwrap(api.POST("/v1/conversations/{conversation_id}/draft", path)) }),
    addToKnowledge: useMutation({
      mutationFn: (body: AddToKnowledgeIn) => unwrap(api.POST("/v1/conversations/{conversation_id}/add-to-knowledge", { ...path, body })),
      ...options,
    }),
    exportTranscript: useMutation({
      mutationFn: (): Promise<Blob> =>
        unwrap(
          api.GET("/v1/conversations/{conversation_id}/export", {
            params: { path: { conversation_id: conversationId }, query: { format: "txt", include_notes: false } },
            parseAs: "blob",
          }),
        ),
    }),
  };
}
